/* eslint-disable array-callback-return */

import AddIcon from '@mui/icons-material/Add';
import { Button, Divider, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	PARTNER_DATEN_NICHT_ERFASST,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem, Validation } from 'src/lib/types/types';
import {
	ForceTrue,
	GetMaxDecimalWithStaticMinValidation,
	NonNegativeDecimalValidation,
	NonRequired,
	Required,
} from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { getFormStrucComponentTable } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, getOptionsFromRes, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
} from '../../../../../../application-helper-function/AppHelperfunctions';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const KP_P = KostenPositionSchema.attributes;

const ELEKTROFAHRZEUGE = 'Elektrofahrzeuge';
const LADE4547TANKINFRASTRUKTUR = 'Lade4547Tankinfrastruktur';
const NETZANSCHLUSSUNDERDARBEITEN = 'NetzanschlussUndErdarbeiten';

const InvestitionkostenAntragEmob: FC<{ kostentraeger: string; unternehmenId?: number; hasComment?: boolean }> = ({
	kostentraeger,
	unternehmenId,
	hasComment,
}) => {
	const { application, setApplicationProperty, readOnly, saveApplication } = useApplication();
	const [kostenKategorie, setKostenKategorie] = useState([]);
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleAddInvestitionsKosten = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
			kostentraeger,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Sachkosten,
		);
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(appCopy, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};

	const handleDeleteInvestitionKosten = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	// Fetch Kostenkategorie
	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter({
					Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
					Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Investitionskosten,
					Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
					Allgemein_Aktiv: { $eq: true },
				}, APIS.Antrag);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategorie', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<>
			<ApplicationHeading title='Investitionskosten' description={PLATZHALTER_TEXT} />
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
				if (
					get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
					get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten &&
					get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Investitionskosten ${get(ele, KP_P.Allgemein_Bezeichnung.path, '')}`}
							onDelete={() => handleDeleteInvestitionKosten(get(ele, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, ele)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapInvestitionsKosten(PruefAssistenzStruc(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path))}
							/>

							<Grid2 container spacing={6}>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapInvestitionsKosten(formStruc1(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
									/>
								</Grid2>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapInvestitionsKosten(formStruc2(getSelectedKostenkatorie(i, application, kostenKategorie)), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
									/>
								</Grid2>
							</Grid2>
							<Divider sx={{ mt: '2rem' }} />

							{getSelectedKostenkatorie(i, application, kostenKategorie) === ELEKTROFAHRZEUGE && (
								<>
									<Typography sx={{ mt: '1rem' }}>Kalkulation Elektrofahrzeug</Typography>

									<CreateTable
										formStruc={mapInvestitionsKosten(elektroFahrzeugStruc1(), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										columnsLabel={['', 'Hersteller / Bezeichnung', 'Grundpreis (€)', 'Spezifische Ausstattung (€)', 'Fahrzeugpreis (€)']}
									/>

									<CreateForm
										formStruc={mapInvestitionsKosten(elektroFahrzeugStruc2, i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
									/>
								</>
							)}
							{getSelectedKostenkatorie(i, application, kostenKategorie) === LADE4547TANKINFRASTRUKTUR && (
								<>
									<Typography sx={{ mt: '1rem' }}>Kalkulation Lade-/Tankinfrastruktur</Typography>
									<Grid2 container spacing={6}>
										<Grid2 sm={6}>
											<CreateForm
												formStruc={mapInvestitionsKosten(ladeInfraStruc(), i)}
												formValues={application}
												updateFormValuesFunc={setApplicationProperty}
												readOnly={updatedReadOnly}
												api={APIS.Kostenposition}
											/>
										</Grid2>
										<Grid2 sm={6}>
											<CreateForm
												formStruc={mapInvestitionsKosten(ladeInfraStruc2(), i)}
												formValues={application}
												updateFormValuesFunc={setApplicationProperty}
												readOnly={updatedReadOnly}
												api={APIS.Kostenposition}
											/>
										</Grid2>
									</Grid2>
								</>
							)}
							{getSelectedKostenkatorie(i, application, kostenKategorie) === NETZANSCHLUSSUNDERDARBEITEN && (
								<>
									<Typography sx={{ mt: '1rem' }}>Kalkulation Netzanschluss und Erdarbeiten</Typography>
									<Grid2 container spacing={6}>
										<Grid2 sm={6}>
											<CreateForm
												formStruc={mapInvestitionsKosten(netzAnschlussStruc(), i)}
												formValues={application}
												updateFormValuesFunc={setApplicationProperty}
												readOnly={updatedReadOnly}
												api={APIS.Kostenposition}
											/>
										</Grid2>
										<Grid2 sm={6}>
											<CreateForm
												formStruc={mapInvestitionsKosten(netzAnschlussStruc2(), i)}
												formValues={application}
												updateFormValuesFunc={setApplicationProperty}
												readOnly={updatedReadOnly}
												api={APIS.Kostenposition}
											/>
										</Grid2>
									</Grid2>
								</>
							)}
							<CreateForm
								formStruc={mapInvestitionsKosten(KostenProJahtStruc(application), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>
							<CreateForm
								formStruc={mapInvestitionsKosten(fomStrucGeplanteSumme(''), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button sx={{ mt: '2rem' }} endIcon={<AddIcon />} onClick={handleAddInvestitionsKosten} disabled={updatedReadOnly}>
					Investitionskosten hinzufügen
				</Button>
			:	<MUIAlert message={PARTNER_DATEN_NICHT_ERFASST} />}
		</>
	);
};

export default InvestitionkostenAntragEmob;

const PruefAssistenzStruc = () => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
};
const getSelectedKostenkatorie = (index: number, application: any, kostenKategorie: any[]) => {
	let selectecKatogorie = get(
		application,
		addIndexesToPath(
			P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie.path,
			[`${index}`],
			['Foerderantrag_Kostenpositions'],
		),
	);

	if (typeof selectecKatogorie === 'object') {
		return get(selectecKatogorie, 'attributes.Allgemein_Ausgabekategorie');
	} else {
		let kategorieObj = kostenKategorie.find((ele) => parseInt(get(ele, 'id')) === parseInt(selectecKatogorie));
		return get(kategorieObj, 'attributes.Allgemein_Ausgabekategorie');
	}
};
const mapInvestitionsKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};
const mapSelectedPath = (parentPath: string, childPath: string, index: number) => {
	const path = childPath.slice(0, parentPath.length) + `.${index}` + childPath.slice(parentPath.length);
	return path;
};

const formStruc1 = (kostenKategorie?: any): FormStrucItem[] => {
	const kategorieOptions = kostenKategorie || [];

	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'enumeration',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const formStruc2 = (selectedKategorie?: string) => {
	let struc: FormStrucItem[] = [
		{
			fields: [P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];

	if (selectedKategorie === LADE4547TANKINFRASTRUKTUR) {
		struc = [];
	}

	return struc;
};
const elektroFahrzeugStruc1 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'Referenzfahrzeug', validation: [{ validateFunction: NonRequired }] },
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
					.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_1_Zelle_1,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_1_Zelle_2,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_1_Zelle_3,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_1_Zelle_4,
					format: 'currency',
					disabled: true,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'Elektrofahrzeug', validation: [{ validateFunction: NonRequired }] },
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
					.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_2_Zelle_1,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_2_Zelle_2,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_2_Zelle_3,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Fahrzeug_Block_2_Zelle_4,
					disabled: true,
					format: 'currency',
				},
			],
		},
	];
	return struc;
};
const elektroFahrzeugStruc2 = [
	{
		fields: [
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
					.Kalkulation_Elektrofahrzeug_Projekbezogene_Ausgaben,
				format: 'currency',
				disabled: true,
				validation: [{ validateFunction: NonNegativeDecimalValidation, disabledValidation: false }],
			},
		],
		userProps: { direction: 'column', columnToDivide: 2 },
	},
];
const ladeInfraStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes
					.Kalkulation_Lade_Tankinf_0105328546_t_Der_Lade_Tankinfrastruktur,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes
						.Kalkulation_Lade_Tankinfrastruktur_Projekbezogene_Ausgaben,
					format: 'currency',
					validation: [{ validateFunction: Required }, { validateFunction: NonNegativeDecimalValidation }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const ladeInfraStruc2 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes
					.Kalkulation_Lade_Tankinf_0142940180_ler_Bezeichnung_Ggf_Standort,
			],
			userProps: { columnToDivide: 1 },
		},
		///////////////////////////////////////////////////////////////////////////////////////
		//////////////////////////////////// BITTE LESEN /////////////////////////////////////
		// DIESE FELDER WURDE NACH DEM TICKET HA-1373 ENTFERNT. IM JF WURDE ES AUCH BESPROCHEN
		// {
		// 	fields: [P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes.Strasse],
		// 	userProps: { columnToDivide: 1 },
		// },
		// {
		// 	fields: [
		// 		{
		// 			...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes.PLZ,
		// 			validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
		// 		},
		// 		P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes.Ort,
		// 	],
		// 	userProps: { columnToDivide: 2 },
		// },
	];

	return struc;
};
const netzAnschlussStruc = (): FormStrucItem[] => {
	return [
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
					.Kalkulation_Netzanschlus_0179561310_r_Zu_Erbringenden_Leistungen,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
						.Kalkulation_Netzanschluss_Und_Erdarbeiten_Kalkulierte_Ausgaben,
					format: 'currency',
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const netzAnschlussStruc2 = (): FormStrucItem[] => {
	return [
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
					.Kalkulation_Netzanschluss_Und_Erdarbeiten_Anzahl_Standorte,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
						.Kalkulation_Netzanschluss_Und_Erdarbeiten_Ausgaben_Pro_Standort,
					format: 'currency',
					disabled: true,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
						.Kalkulation_Netzanschlus_0089722798_iten_Projekbezogene_Ausgaben,
					format: 'currency',
					disabled: true,
					validation: [{ validateFunction: NonNegativeDecimalValidation, disabledValidation: false }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const KostenProJahtStruc = (application: any) => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
					type: 'componentTable',
					maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
					disabledColumns: ['Zelle_1'],
					fieldProps: { Zelle_2: { format: 'currency' } },
					hideDeleteButton: true,
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
};
const fomStrucGeplanteSumme = (selectedProjektbezogeneAusgabenPath: string) => {
	let validation: Validation[] = [];
	if (selectedProjektbezogeneAusgabenPath) {
		validation = [
			{ validateFunction: Required, disabledValidation: false },
			{
				validateFunction: GetMaxDecimalWithStaticMinValidation,
				params: [
					selectedProjektbezogeneAusgabenPath,
					'',
					'Der Vergleichswert darf nicht negativ sein (siehe Projektbezogene Ausgaben).',
					'Projektbezogene Ausgaben muss für einen Vergleich gesetzt werden.',
				],
				disabledValidation: false,
			},
		];
	}
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme,
					format: 'currency',
					disabled: true,
					validation,
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
};

export function getFormStrucInvestitionkostenAntragEmob(application: any, unternehmenId: number, kostenKategorie: any): FormStrucItem[] {
	let formStruc: FormStrucItem[] = [];
	get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
		) {
			let selectedKostenKategorie = getSelectedKostenkatorie(i, application, kostenKategorie);
			let selectedProjektbezogeneAusgabenPath = '';
			const formStruc2WithIndex = mapInvestitionsKosten(formStruc2(), i);

			formStruc = concat(formStruc, formStruc2WithIndex);
			if (selectedKostenKategorie === ELEKTROFAHRZEUGE) {
				selectedProjektbezogeneAusgabenPath = mapSelectedPath(
					P.Foerderantrag_Kostenpositions.path,
					P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Elektrofahrzeug.schema.attributes
						.Kalkulation_Elektrofahrzeug_Projekbezogene_Ausgaben.path,
					i,
				);

				const elektroFahrzeugStruc1WithIndex = mapInvestitionsKosten(elektroFahrzeugStruc1(), i);
				const elektroFahrzeugStruc2WithIndex = mapInvestitionsKosten(elektroFahrzeugStruc2, i);
				formStruc = concat(formStruc, elektroFahrzeugStruc1WithIndex, elektroFahrzeugStruc2WithIndex);
			}
			if (selectedKostenKategorie === LADE4547TANKINFRASTRUKTUR) {
				selectedProjektbezogeneAusgabenPath = mapSelectedPath(
					P.Foerderantrag_Kostenpositions.path,
					P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Lade_Tankinfrastruktur.schema.attributes
						.Kalkulation_Lade_Tankinfrastruktur_Projekbezogene_Ausgaben.path,
					i,
				);
				const ladeInfraStrucWithIndex = mapInvestitionsKosten(ladeInfraStruc(), i);
				const ladeInfraStruc2WithIndex = mapInvestitionsKosten(ladeInfraStruc2(), i);
				formStruc = concat(formStruc, ladeInfraStrucWithIndex, ladeInfraStruc2WithIndex);
			}
			if (selectedKostenKategorie === NETZANSCHLUSSUNDERDARBEITEN) {
				selectedProjektbezogeneAusgabenPath = mapSelectedPath(
					P.Foerderantrag_Kostenpositions.path,
					P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
						.Kalkulation_Netzanschlus_0089722798_iten_Projekbezogene_Ausgaben.path,
					i,
				);
				const netzAnschlussStrucWithIndex = mapInvestitionsKosten(netzAnschlussStruc(), i);
				const netzAnschlussStruc2WithIndex = mapInvestitionsKosten(netzAnschlussStruc2(), i);
				formStruc = concat(formStruc, netzAnschlussStrucWithIndex, netzAnschlussStruc2WithIndex);
			}
			const formStruc1WithIndex = mapInvestitionsKosten(formStruc1(), i);
			const formStrucCTWithIndex = getFormStrucComponentTable(application, mapInvestitionsKosten(KostenProJahtStruc(application), i));
			const formStrucGeplanteSummeWithIndex = mapInvestitionsKosten(fomStrucGeplanteSumme(selectedProjektbezogeneAusgabenPath), i);
			formStruc = concat(formStruc, formStrucCTWithIndex, formStruc1WithIndex, formStrucGeplanteSummeWithIndex);

			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);
			if (isHinweisFieldRequired) formStruc = concat(formStruc, mapInvestitionsKosten(PruefAssistenzStruc(), i));
		}
	});
	return formStruc;
}
